import {
  TOP_TIER,
  BOTTOM_TIER,
  promoteCountForGroup,
  relegateCountForGroup,
  tierName,
  type Group
} from '@h2h/shared';
import { DivisionHero } from '../DivisionStrip';
import { GroupStandingsBoard } from '../StandingsRow';
import { DivisionsHelp } from '../DivisionsHelpSheet';
import { cap } from '../../../lib/format';
import { groupNumber, zoneForRank } from '../../../lib/round';

export function LiveRoundStandingsView({
  group,
  uid,
  roundLabel,
  playerTier,
  live = false,
  liveProgress = null
}: {
  group: Group;
  uid: string;
  roundLabel: string;
  /** The player's CURRENT ladder rung (membership.tier). Overlapping rounds in a
   *  day-chunked tournament can be grouped before an earlier round settles, so the
   *  group's formation tier (`group.tier`) may lag the player's real rung after a
   *  promotion. We show the membership tier so this matches the Season tab; the
   *  group is still scored against the live tier at settlement either way. */
  playerTier?: number;
  /** Round is still in play (OPEN/LOCKING/SETTLING) — drives the LIVE/provisional
   *  banner and the "fresh group" marker so a live ranking never reads as final. */
  live?: boolean;
  /** Fixtures settled vs total this round, for the "x/n matches in" live banner. */
  liveProgress?: { settled: number; total: number } | null;
}) {
  const size = group.memberIds.length;
  const tier = playerTier ?? group.tier;
  const promote = promoteCountForGroup(size);
  const relegate = relegateCountForGroup(size);
  const me = group.standings.find((r) => r.userId === uid);
  const myZone = me ? zoneForRank(me.rankInGroup, size) : null;

  const atTop = tier >= TOP_TIER;
  const atBottom = tier <= BOTTOM_TIER;
  const upTier = cap(tierName(Math.min(TOP_TIER, tier + 1)));
  const downTier = cap(tierName(Math.max(BOTTOM_TIER, tier - 1)));

  return (
    <div className="flex flex-col gap-4">
      <DivisionHero
        tierIndex={tier}
        groupLabel={`Group ${groupNumber(group.groupId)}`}
        playerCount={size}
        roundLabel={roundLabel}
        myRank={me?.rankInGroup ?? null}
        myScore={me?.roundScore ?? null}
        myZone={myZone}
        myMovement={me?.movement ?? null}
        promoteCount={promote}
        relegateCount={relegate}
        live={live}
        liveProgress={liveProgress}
      />

      <GroupStandingsBoard
        standings={group.standings}
        uid={uid}
        size={size}
        promoteTarget={atTop ? undefined : upTier}
        relegateTarget={atBottom ? undefined : downTier}
      />

      <DivisionsHelp tierIndex={tier} variant="inline" className="self-start" />
    </div>
  );
}
